import { orderSourceLabel } from '@/utils/orderSource';
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { formatDeliveryDateDisplay } from '../utils/dateUtils';
import { formatPhoneNumberDisplay } from '../utils/phoneUtils';
import StatusBadge from '@/components/StatusBadge';
import { formatOrderValue } from '@/utils/orderValue';

// Read-only counterpart to OrderRow, for the Operations dashboard's
// expandable pipeline rows. No edit/delete — this is a glance view, not a
// second place orders get managed from.
const PipelineOrderRow = ({ order }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const formattedOrderValue = formatOrderValue(order.total_price);

  return (
    <div className="border rounded-xl overflow-hidden bg-white" data-testid={`pipeline-order-item-${order.id}`}>
      <div
        className="flex items-center p-2 cursor-pointer hover:bg-stone-50 transition-colors"
        onClick={() => setIsExpanded((v) => !v)}
      >
        <button className="flex-shrink-0 text-stone-400 mr-2">
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0 text-sm">
          {order.internal_order_number && (
            <span className="px-1 py-0.5 bg-primary/10 text-primary text-xs font-mono rounded flex-shrink-0">
              {order.internal_order_number}
            </span>
          )}
          <StatusBadge status={order.status || 'designed'} size="sm" className="flex-shrink-0" />
          <span className="font-medium text-stone-900 truncate">
            {order.recipient_name || 'Unknown'}
          </span>
          <span className="text-stone-500 hidden sm:inline">|</span>
          <span className="text-primary font-medium hidden sm:inline flex-shrink-0">
            {orderSourceLabel(order, 'N/A')}
          </span>
          <span className="text-stone-500 hidden lg:inline">|</span>
          <span className="text-stone-600 hidden lg:inline flex-shrink-0">
            #{order.order_number || 'N/A'}
          </span>
        </div>
        {/* Matches the right-aligned value in All Orders' date groups
            (TCA-26082301) so the same figure reads the same way on both
            screens. `tabular-nums` keeps the decimal points aligned down the
            column. */}
        <span
          className="min-w-[5rem] flex-shrink-0 ml-2 text-right text-sm font-semibold text-stone-700 tabular-nums"
          data-testid={`pipeline-order-value-${order.id}`}
          title={formattedOrderValue === '—' ? 'Order value unavailable' : 'Order value'}
        >
          {formattedOrderValue}
        </span>
      </div>

      {isExpanded && (
        <div className="px-2 pb-2 pt-1 border-t bg-stone-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm pl-6">
            <div className="sm:hidden">
              <span className="font-medium text-stone-700">Source: </span>
              <span className="text-primary">{orderSourceLabel(order, 'N/A')}</span>
            </div>
            <div className="lg:hidden">
              <span className="font-medium text-stone-700">Order #: </span>
              <span className="text-stone-900">{order.order_number || 'N/A'}</span>
            </div>
            <div>
              <span className="font-medium text-stone-700">Delivery: </span>
              <span className="text-stone-900">
                {order.delivery_date ? formatDeliveryDateDisplay(order.delivery_date) : 'N/A'}
              </span>
            </div>
            <div>
              <span className="font-medium text-stone-700">Street Address: </span>
              <span className="text-stone-900">
                {order.recipient_street_address
                  ? [order.recipient_street_address, order.recipient_city, order.recipient_state, order.recipient_zip_code].filter(Boolean).join(', ')
                  : 'N/A'}
              </span>
            </div>
            <div>
              <span className="font-medium text-stone-700">Phone: </span>
              <span className="text-stone-900">{formatPhoneNumberDisplay(order.recipient_phone) || 'N/A'}</span>
            </div>
            <div>
              <span className="font-medium text-stone-700">Apt, Suite, Unit, etc.: </span>
              <span className="text-stone-900">{order.recipient_street_address_2 || 'N/A'}</span>
            </div>
            <div>
              <span className="font-medium text-stone-700">Accepted: </span>
              <span className="text-stone-900">
                {order.order_accepted_date ? formatDeliveryDateDisplay(order.order_accepted_date) : 'N/A'}
              </span>
            </div>
            {order.card_message && (
              <div className="md:col-span-2">
                <span className="font-medium text-stone-700">Card Message: </span>
                <span className="text-stone-900 italic whitespace-pre-line">"{order.card_message}"</span>
              </div>
            )}
            {order.special_instructions && (
              <div className="md:col-span-2">
                <span className="font-medium text-stone-700">Special Instructions: </span>
                <span className="text-stone-900">{order.special_instructions}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineOrderRow;
